import type { DatabaseBlockModel } from '@blocksuite/affine-model';
import type { BlockModel, Doc, DocMeta, Workspace } from '@blocksuite/store';

export interface DatabaseTargetCandidate {
  docId: string;
  docTitle: string;
  blockId: string;
  blockTitle: string;
}

export const isDatabaseBlock = (
  block: BlockModel
): block is DatabaseBlockModel => block.flavour === 'affine:database';

const walkForDatabases = (
  root: BlockModel | undefined,
  acc: DatabaseBlockModel[]
) => {
  if (!root) return;
  if (isDatabaseBlock(root)) acc.push(root);
  for (const child of root.children) {
    walkForDatabases(child, acc);
  }
};

export const collectDatabasesFromDoc = (doc: Doc): DatabaseBlockModel[] => {
  const acc: DatabaseBlockModel[] = [];
  try {
    const store = doc.getStore({ id: doc.id });
    const root = store?.root;
    if (!root) return acc;
    walkForDatabases(root, acc);
  } catch (err) {
    console.warn('[orghub-database] skip doc walk', doc.id, err);
  }
  return acc;
};

export const databaseTitleOf = (model: DatabaseBlockModel): string => {
  const text = model.props.title?.toString().trim();
  return text && text.length > 0 ? text : 'Untitled database';
};

export const docTitleOf = (workspace: Workspace, docId: string): string =>
  workspace.meta.getDocMeta(docId)?.title?.trim() || 'Untitled doc';

/**
 * Collect every database block of every *loaded* doc in the workspace.
 *
 * Unloaded docs are skipped on purpose — loading the whole workspace would
 * be far too expensive. Use `collectUnloadedDocs` + `loadDocs` to pull in
 * more docs on demand.
 */
export const collectAllDatabases = (
  workspace: Workspace
): DatabaseTargetCandidate[] => {
  const candidates: DatabaseTargetCandidate[] = [];
  for (const [docId, doc] of workspace.docs) {
    if (!doc.loaded) continue;
    const meta = workspace.meta.getDocMeta(docId);
    if (meta?.trash) continue;
    let dbs: DatabaseBlockModel[];
    try {
      dbs = collectDatabasesFromDoc(doc);
    } catch (err) {
      console.warn('[orghub-database] skip doc', docId, err);
      continue;
    }
    if (dbs.length === 0) continue;
    const docTitle = meta?.title?.trim() || 'Untitled doc';
    for (const db of dbs) {
      candidates.push({
        docId,
        docTitle,
        blockId: db.id,
        blockTitle: databaseTitleOf(db),
      });
    }
  }
  return candidates;
};

/**
 * Docs that have not been loaded yet, most recently updated first.
 */
export const collectUnloadedDocs = (workspace: Workspace): DocMeta[] => {
  const all = workspace.meta.docMetas.filter(m => !m.trash);
  return all
    .filter(m => {
      const doc = workspace.getDoc(m.id);
      return !doc?.loaded;
    })
    .sort(
      (a, b) =>
        (b.updatedDate ?? b.createDate) - (a.updatedDate ?? a.createDate)
    );
};

export const loadDocs = (docs: Doc[]): Promise<void> =>
  new Promise(resolve => {
    if (docs.length === 0) {
      resolve();
      return;
    }
    let remaining = docs.length;
    const done = () => {
      remaining--;
      if (remaining === 0) resolve();
    };
    docs.forEach(doc => {
      if (doc.loaded) done();
      else doc.load(done);
    });
  });
